import React, { useMemo } from 'react';
import { TrendingUp, ShoppingBag, Users, MessageSquare, Plus, ArrowUpRight, Package, DollarSign, Eye, Save } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { generateListings, generateUsers, generateVendors } from '../utils/mockData';
import { formatCurrency, cn } from '../lib/utils';
import { resolveGem } from '../constants/sampleImages';
import { Link } from 'react-router-dom';

const REVENUE_DATA = [
  { day: 'Mon', value: 450000 },
  { day: 'Tue', value: 820000 },
  { day: 'Wed', value: 610000 },
  { day: 'Thu', value: 950000 },
  { day: 'Fri', value: 1200000 },
  { day: 'Sat', value: 1450000 },
  { day: 'Sun', value: 1100000 },
];

export const VendorDashboard = () => {
  const data = useMemo(() => {
    const users = generateUsers(1);
    const vendors = generateVendors(users);
    const listings = generateListings(vendors, 10);
    return { vendor: vendors[0], listings };
  }, []);

  return (
    <div className="space-y-8 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Business Command</h1>
          <p className="text-slate-500 font-medium">{data.vendor.companyName} • Beruwala, Sri Lanka</p>
        </div>
        <Link 
          to="/add-listing"
          className="bg-primary text-white px-6 py-3 rounded-2xl font-black shadow-xl shadow-primary/20 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
        >
          <Plus size={20} />
          New Listing
        </Link>
      </div>

      {/* Analytics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatCard label="Total Revenue" value={formatCurrency(4580000)} icon={DollarSign} trend="+12.4%" color="bg-primary/10 text-primary" />
        <StatCard label="Active Items" value="14" icon={Package} trend="+2" color="bg-primary/5 text-primary" />
        <StatCard label="Profile Views" value="2.8k" icon={Eye} trend="+45%" color="bg-amber-50 text-amber-600" />
        <StatCard label="New Inquiries" value="28" icon={MessageSquare} trend="+5" color="bg-primary/5 text-primary" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Sales Performance */}
        <div className="lg:col-span-8 bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-black text-slate-900 text-lg uppercase tracking-widest">Sales Performance</h2>
            <select className="bg-slate-50 border-none rounded-xl text-xs font-bold p-2 focus:ring-primary/20">
              <option>Last 7 Days</option>
              <option>Last 30 Days</option>
            </select>
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={REVENUE_DATA}>
                <defs>
                   <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#CC10FE" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#CC10FE" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis 
                  dataKey="day" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }} 
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }}
                  tickFormatter={(val) => `Rs. ${val/1000}k`}
                />
                <Tooltip 
                  contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#CC10FE" 
                  strokeWidth={4} 
                  fillOpacity={1} 
                  fill="url(#colorSales)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Inventory */}
        <div className="lg:col-span-4 bg-white p-8 rounded-3xl border border-slate-200 shadow-sm flex flex-col">
          <h2 className="font-black text-slate-900 text-lg uppercase tracking-widest mb-6">Inventory</h2>
          <div className="space-y-4 flex-1">
            {data.listings.slice(0, 5).map((listing) => (
              <div key={listing.id} className="flex gap-4 group cursor-pointer">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-50 shrink-0 border border-slate-100">
                  <img src={resolveGem(listing.images?.[0])} alt={listing.title || 'Gem listing'} className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-900 truncate text-sm">
                    {listing.title || `${listing.variety} ${listing.type}`}
                  </p>
                  <p className="text-xs text-slate-500">
                    {listing.caratWeight || listing.weight}ct • {listing.origin}
                  </p>
                  <p className="text-accent font-black text-sm mt-1">{formatCurrency(listing.price)}</p>
                </div>
              </div>
            ))}
          </div>
          <button className="w-full mt-8 py-4 bg-slate-50 hover:bg-slate-100 text-slate-600 font-bold rounded-2xl transition-all text-xs uppercase tracking-widest border border-slate-100">
            Full Inventory
          </button>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, trend, color }) => (
  <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm group hover:border-primary/30 transition-all">
    <div className="flex justify-between items-start mb-4">
      <div className={cn("p-3 rounded-2xl transition-transform group-hover:scale-110", color)}>
        <Icon size={24} />
      </div>
      <span className="text-[10px] font-black text-primary bg-primary/5 px-2 py-1 rounded-lg">
        {trend}
      </span>
    </div>
    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{label}</p>
    <p className="text-2xl font-black text-slate-900 tracking-tight">{value}</p>
  </div>
);
